/**
 * Local-first persistence for a blob document (a spreadsheet or a slide deck).
 *
 * Mirrors `use-page-editor` but for a SINGLE serialized content block instead
 * of many TipTap blocks:
 *  - Initial load: the page's content block → model (via `fromBlock`).
 *  - Autosave: every model change is debounced and persisted with
 *    `syncPageBlocksMutation` (one stable-id row = one upsert, one PowerSync tx
 *    = one version snapshot).
 *  - Remote changes: the reactive blocks query detects rows synced from another
 *    device; when the user is idle (not mid-edit) and the content differs, the
 *    model is refreshed (last-write-wins, resolved by the server).
 */
import { createEffect, createSignal, onCleanup, untrack } from "solid-js";
import type { Block } from "@folio/powersync";
import { type EditorBlockNode, syncPageBlocksMutation } from "@folio/shared";
import { usePageBlocksQuery } from "@folio/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { pickContentBlock } from "../lib/document-blocks";

const AUTOSAVE_DEBOUNCE_MS = 600;
// While the user is actively editing, ignore remote refreshes for a short
// window so a background sync can't clobber in-progress typing.
const REMOTE_IDLE_MS = 1500;

interface BlobDocumentOptions<T> {
  pageId: () => string;
  fromBlock: (block: Block | undefined) => T;
  toBlockNode: (pageId: string, model: T) => EditorBlockNode;
}

export function useBlobDocument<T>(options: BlobDocumentOptions<T>) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();

  const [model, setModel] = createSignal<T | undefined>();
  const [isReady, setIsReady] = createSignal(false);
  const [isSaving, setIsSaving] = createSignal(false);
  const [lastSavedAt, setLastSavedAt] = createSignal<number | null>(null);
  const [saveError, setSaveError] = createSignal(false);

  const blocks = usePageBlocksQuery({
    db: () => db(),
    drizzle,
    pageId: () => options.pageId(),
  });

  /** Content string last persisted or loaded (remote-change detector). */
  let lastSyncedContent = "";
  let lastLocalEditAt = 0;
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  let saveChain: Promise<void> = Promise.resolve();

  const contentBlock = (): Block | undefined => {
    const rows = blocks.data;
    return rows ? pickContentBlock(rows, untrack(options.pageId)) : undefined;
  };

  const persist = (current: T) => {
    const dbInstance = drizzle();
    const uid = userId();
    const id = untrack(options.pageId);
    if (!dbInstance || !uid) {
      return;
    }
    const node = options.toBlockNode(id, current);
    if (node.content === lastSyncedContent) {
      return;
    }
    lastSyncedContent = node.content;
    setIsSaving(true);
    setSaveError(false);
    saveChain = saveChain
      .then(async () => {
        await syncPageBlocksMutation(dbInstance, id, uid, [node]);
        setLastSavedAt(Date.now());
        return undefined;
      })
      .catch((err: unknown) => {
        setSaveError(true);
        console.error(
          "[documents] Error saving document:",
          err instanceof Error ? err.message : String(err),
        );
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const flush = () => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    const current = untrack(model);
    if (current !== undefined) {
      persist(current);
    }
  };

  /** Apply a user edit: update the model and schedule a debounced save. */
  const update = (next: T) => {
    lastLocalEditAt = Date.now();
    setModel(() => next);
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    saveTimeout = setTimeout(() => {
      saveTimeout = null;
      persist(next);
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  // Initial load: once the content block is available, seed the model.
  createEffect(() => {
    if (blocks.isLoading || isReady()) {
      return;
    }
    const block = contentBlock();
    const initial = options.fromBlock(block);
    lastSyncedContent = options.toBlockNode(untrack(options.pageId), initial).content;
    setModel(() => initial);
    setIsReady(true);
  });

  // Remote refresh: content synced from elsewhere while the user is idle.
  createEffect(() => {
    const rows = blocks.data;
    if (!rows || !isReady()) {
      return;
    }
    const block = pickContentBlock(rows, untrack(options.pageId));
    const remoteContent = block?.content ?? "";
    if (remoteContent === "" || remoteContent === lastSyncedContent) {
      return;
    }
    if (Date.now() - lastLocalEditAt < REMOTE_IDLE_MS) {
      return;
    }
    lastSyncedContent = remoteContent;
    setModel(() => options.fromBlock(block));
  });

  // Flush the pending debounced save on unmount (navigation).
  onCleanup(() => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
      const current = untrack(model);
      if (current !== undefined) {
        persist(current);
      }
    }
  });

  return {
    isReady,
    model,
    update,
    flush,
    isSaving,
    lastSavedAt,
    saveError,
    contentBlocks: () => blocks.data ?? [],
  };
}
